import React, { useState, useEffect } from 'react';
import { InputField, Dropdown, Form, Button, StickySaveBar } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'data';
import { editAddress, fetchAddress } from 'data/slices/profileSlice'; // Adjust path if needed
import { useFormChanges } from 'hooks';
import { countries } from 'helpers';
import { useTranslation } from 'react-i18next';

interface PersonalDataFormData {
  street: string;
  houseNumber: string;
  postCode: string;
  city: string;
  country: string;
}

interface FormSaveHandler {
  save: () => Promise<void>;
  hasChanges: () => boolean;
}

export const PersonalData = ({ user, saveRef }: { user: any; saveRef?: React.MutableRefObject<FormSaveHandler | null> }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.profile);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState<PersonalDataFormData>({
    street: '',
    houseNumber: '',
    postCode: '',
    city: '',
    country: '',
  });
  
  const [initialFormData, setInitialFormData] = useState<PersonalDataFormData>({
    street: '',
    houseNumber: '',
    postCode: '',
    city: '',
    country: '',
  });
  
  useEffect(() => {
    if (user) {
      const newData = {
        street: user.addresses[0].Street || '',
        houseNumber: user.addresses[0].HouseNumber || '',
        postCode: user.addresses[0].PostCode || '',
        city: user.addresses[0].City || '',
        country: user.addresses[0].Country || '',
      };
      setFormData(newData);
      setInitialFormData(newData);
    }
  }, [user]);
  
  const isFormChanged = useFormChanges(initialFormData, formData);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveAddress = async () => {
	setSubmitted(true);
	if (formData.street == '' || 
		formData.houseNumber == '' ||
		formData.city == '' ||
		formData.country == '') {
		return
	}
    const payload = {
      Street: formData.street,
      HouseNumber: formData.houseNumber,
      PostCode: formData.postCode,
      City: formData.city,
      Country: formData.country,
    };

    try {
      const response = await dispatch(editAddress(payload)).unwrap();
      console.log(t('personalData.toast.addressUpdatedSuccessfully'), response);

      await dispatch(fetchAddress()).unwrap();
      console.log('Address refetched successfully');

      // Sync initialFormData after save
      setInitialFormData({ ...formData });
    } catch (err) {
      console.error(t('personalData.toast.addressUpdateFailed'), err);
    }
  };

  // Expose save handler and form change status via ref
  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        save: handleSaveAddress,
        hasChanges: () => isFormChanged,
      };
    }
  }, [saveRef, isFormChanged, handleSaveAddress]);

  const countryOptions = [
    { label: t('personalData.select'), value: '' },
    ...countries.map((country) => ({
      label: t(country.labelKey),
      value: country.value,
    })),
  ];

  return (
    <>
      <Form
        content={
          <form className="flex flex-col gap-4">
            <div className="text-[20px] font-medium">
              {t('personalData.title')}
            </div>
            <InputField
              label={t('personalData.street')}
              name="street"
              value={formData.street}
              onChange={handleChange}
			  error={submitted && !formData.street ? t('general.required') as string : undefined}
            />
            <InputField
              label={t('personalData.houseNumber')}
              name="houseNumber"
              value={formData.houseNumber}
              onChange={handleChange}
              type="text"
			  error={submitted && !formData.houseNumber ? t('general.required') as string : undefined}
            />
            <InputField
              label={t('personalData.postCode')}
              name="postCode"
              value={formData.postCode}
              onChange={handleChange}
			  error={submitted && !formData.postCode ? t('general.required') as string : undefined}
            />
            <InputField
              label={t('personalData.city')}
              name="city"
              value={formData.city || user.addresses[0].city || ''}
              onChange={handleChange}
			  error={submitted && !formData.city ? t('general.required') as string : undefined}
            />
            <Dropdown
              label={t('personalData.country')}
              name="country"
              value={formData.country || user.addresses[0].country || ''}
              options={countryOptions}
              onChange={handleDropdownChange}
			  error={submitted && !formData.country ? t('general.required') as string : undefined}
            />
          </form>
        }
        maxWidth={608}
      />
      <StickySaveBar>
        <Button
          onClick={handleSaveAddress}
          label={t('personalData.saveChanges')}
          loading={loading}
          disabled={loading || !isFormChanged}
          style={{ opacity: loading || !isFormChanged ? 0.5 : 1 }}
        />
      </StickySaveBar>
    </>
  );
};
